// Shared benefit-detail formatting + label maps.
// Extracted from PlanComparisonDialog so both the comparison dialog and the
// per-type benefit renderer (summary review) format identically and share the
// same react-query cache keys. Keep the null/"—" semantics stable — the
// comparison dialog's medical rows depend on them.

// Benefit-category keys (medical, prescription, specialist, …) → display label.
export const BENEFIT_LABELS: Record<string, string> = {
  medical: "Medical",
  prescription: "Prescription",
  preventive: "Preventive Care",
  emergency: "Emergency",
  hospital: "Hospital",
  specialist: "Specialist",
  primaryCare: "Primary Care",
  urgentCare: "Urgent Care",
  urgent_care: "Urgent Care",
  mentalHealth: "Mental Health",
  mental_health: "Mental Health",
  substance_abuse: "Substance Abuse",
  maternity: "Maternity",
  surgery: "Surgery",
  vision: "Vision",
  vision_exam: "Vision — Exam",
  vision_frames: "Vision — Frames",
  vision_lenses: "Vision — Lenses",
  dental: "Dental",
  dental_basic: "Dental — Basic",
  dental_major: "Dental — Major",
  orthodontia: "Orthodontia",
};

export function fmtMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function fmtPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${Number(value)}%`;
}

export function benefitLabel(key: string): string {
  return (
    BENEFIT_LABELS[key] ||
    key.replace(/[_-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
  );
}

// One category's stored benefit values (a single row of plan_benefits, as
// reshaped by GET /api/plan-benefits/:planId).
export interface MedicalBenefitCategory {
  description?: string;
  inNetworkDeductible?: number;
  outNetworkDeductible?: number;
  inNetworkCoinsurance?: number;
  outNetworkCoinsurance?: number;
  inNetworkCopay?: number;
  outNetworkCopay?: number;
  maxOutOfPocket?: number;
  annualMaximum?: number;
}

export type MedicalBenefits = Record<string, MedicalBenefitCategory>;

// Ordered field list driving the per-category detail rows for the single-plan
// renderer. (The comparison dialog keeps its own column layout in BenefitGroup;
// this shares only the field knowledge, not the JSX.)
type FieldFormat = "money" | "percent" | "notes";

export const MEDICAL_BENEFIT_FIELDS: ReadonlyArray<{
  label: string;
  accessor: keyof MedicalBenefitCategory;
  format: FieldFormat;
}> = [
  { label: "In-Network Deductible", accessor: "inNetworkDeductible", format: "money" },
  { label: "In-Network Copay", accessor: "inNetworkCopay", format: "money" },
  { label: "In-Network Coinsurance", accessor: "inNetworkCoinsurance", format: "percent" },
  { label: "Max Out-of-Pocket", accessor: "maxOutOfPocket", format: "money" },
  { label: "Out-of-Network Deductible", accessor: "outNetworkDeductible", format: "money" },
  { label: "Out-of-Network Copay", accessor: "outNetworkCopay", format: "money" },
  { label: "Annual Maximum", accessor: "annualMaximum", format: "money" },
];

export function formatMedicalField(
  value: number | string | null | undefined,
  format: FieldFormat,
): string {
  if (format === "percent") return fmtPercent(value as number);
  if (format === "notes") return value != null && value !== "" ? String(value) : "—";
  return fmtMoney(value as number);
}

// A category is worth rendering only if at least one numeric field or a
// description is present (avoids all-"—" groups for sparse W&B data).
export function categoryHasData(category: MedicalBenefitCategory | undefined): boolean {
  if (!category) return false;
  const hasDescription = !!category.description && category.description !== "";
  const hasNumber = MEDICAL_BENEFIT_FIELDS.some((field) => {
    const value = category[field.accessor];
    return value != null && !Number.isNaN(Number(value));
  });
  return hasDescription || hasNumber;
}
